import React, { useState, useEffect } from 'react';

/**
 * SuccessToast — spring slide-in from bottom-right with aurora color pulse.
 *
 * Props:
 *   visible   {boolean}  — show/hide the toast
 *   message   {string}   — main message text
 *   subtext   {string}   — optional secondary line
 *   onDismiss {function} — called after auto-dismiss animation completes
 *   duration  {number}   — ms before auto-dismiss starts (default 3800)
 */
const SuccessToast = ({ visible, message, subtext, onDismiss, duration = 3800 }) => {
  const [phase, setPhase] = useState('idle'); // 'idle' | 'enter' | 'hold' | 'exit'

  useEffect(() => {
    if (!visible) {
      setPhase('idle');
      return;
    }
    setPhase('enter');
    const holdTimer = setTimeout(() => setPhase('hold'), 520);
    const exitTimer = setTimeout(() => setPhase('exit'), duration);
    const doneTimer = setTimeout(() => {
      setPhase('idle');
      onDismiss?.();
    }, duration + 400);
    return () => {
      clearTimeout(holdTimer);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [visible, duration]);

  if (phase === 'idle') return null;

  const animClass = phase === 'exit' ? 'toast-spring-exit' : 'toast-spring-enter';

  return (
    <div
      className={`${animClass} toast-aurora-pulse`}
      style={{
        position: 'fixed',
        bottom: '28px',
        right: '28px',
        zIndex: 99999,
        pointerEvents: 'none',
        borderRadius: '4px',
        border: '1.5px solid #F5A623',
        background: 'linear-gradient(135deg, rgba(10,18,32,0.97) 0%, rgba(15,22,38,0.97) 100%)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '16px 22px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        minWidth: '280px',
        maxWidth: '380px',
      }}
      role="status"
      aria-live="polite"
    >
      {/* Aurora gradient top stripe */}
      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: '2px',
        background: 'linear-gradient(90deg, #F5A623 0%, #2EC4A0 50%, #F5A623 100%)',
        backgroundSize: '200% 100%',
        animation: 'auroraShift 2.4s ease-in-out infinite',
        borderRadius: '4px 4px 0 0',
      }} />

      {/* Animated check icon */}
      <div
        className="toast-icon-pop"
        style={{
          width: '34px',
          height: '34px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(46,196,160,0.12)',
          border: '1.5px solid rgba(46,196,160,0.4)',
          borderRadius: '3px',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <polyline
            points="4,12 9,17 20,6"
            stroke="#2EC4A0"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="60"
            strokeDashoffset="0"
            style={{ animation: 'cfCheckDraw 0.45s ease 0.18s both' }}
          />
        </svg>
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Syne, sans-serif',
          fontSize: '0.8125rem',
          fontWeight: 700,
          color: '#EEF0F2',
          letterSpacing: '0.01em',
          marginBottom: subtext ? '3px' : 0,
        }}>
          {message || 'Vellykket!'}
        </div>
        {subtext && (
          <div style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: '0.75rem',
            color: 'rgba(238,240,242,0.55)',
          }}>
            {subtext}
          </div>
        )}
      </div>

      {/* Orange accent dot */}
      <div style={{
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        background: '#F5A623',
        flexShrink: 0,
        animation: 'auroraBorderPulse 2.4s ease-in-out infinite',
      }} />
    </div>
  );
};

export default SuccessToast;
